import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Storefront, ShoppingBag, MapPin, ClockCounterClockwise, CheckCircle, Truck, XCircle, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { key: "nuevo", label: "Nuevo", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  { key: "confirmado", label: "Confirmado", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/30" },
  { key: "preparando", label: "Preparando", color: "text-indigo-400", bg: "bg-indigo-500/10 border-indigo-500/30" },
  { key: "en_camino", label: "En camino", color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30" },
  { key: "entregado", label: "Entregado", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  { key: "cancelado", label: "Cancelado", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.key, s]));

const STATUS_ICON = {
  nuevo: ClockCounterClockwise,
  confirmado: CheckCircle,
  preparando: ShoppingBag,
  en_camino: Truck,
  entregado: CheckCircle,
  cancelado: XCircle,
};

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function printTicket(order) {
  const itemsRows = (order.items || []).map((i) => `
    <tr>
      <td style="padding:2px 4px;">${i.qty}×</td>
      <td style="padding:2px 4px;">${escapeHtml(i.name)}</td>
      <td style="padding:2px 4px;text-align:right;">$${(i.qty * i.price).toFixed(2)}</td>
    </tr>`).join("");
  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Ticket #${order.order_number || order.id.slice(0, 8)}</title>
<style>
  @page { margin: 6mm; size: 80mm auto; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; color: #000; margin: 0; padding: 8px; }
  .center { text-align: center; }
  .title { font-size: 14px; font-weight: 700; letter-spacing: 1px; }
  .muted { color: #444; font-size: 11px; }
  .sep { border-top: 1px dashed #000; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  .row { display: flex; justify-content: space-between; font-size: 12px; }
  .total { font-size: 15px; font-weight: 700; }
  .field { margin: 3px 0; }
  .label { color: #444; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
  @media print { .noprint { display: none; } }
  .btn { background: #000; color: #fff; padding: 6px 12px; border: 0; cursor: pointer; font-family: inherit; font-size: 12px; }
</style></head>
<body>
  <div class="center title">TICKET DE PEDIDO</div>
  <div class="center muted">#${order.order_number || order.id.slice(0, 8).toUpperCase()} · ${fmtDate(order.created_at)}</div>
  <div class="sep"></div>
  <div class="field"><div class="label">Cliente</div>${escapeHtml(order.contact_name) || "-"}</div>
  <div class="field"><div class="label">Telefono</div>${escapeHtml(order.contact_phone) || "-"}</div>
  <div class="sep"></div>
  <div class="label" style="margin-bottom:4px;">Pedido</div>
  <table>${itemsRows || '<tr><td colspan="3" class="muted">Sin items</td></tr>'}</table>
  <div class="sep"></div>
  <div class="field"><div class="label">Direccion</div>${escapeHtml(order.address) || "-"}</div>
  <div class="sep"></div>
  ${order.subtotal !== order.total ? `<div class="row"><span>Subtotal</span><span>$${Number(order.subtotal).toFixed(2)}</span></div>` : ""}
  ${order.delivery_fee > 0 ? `<div class="row"><span>Envio</span><span>$${Number(order.delivery_fee).toFixed(2)}</span></div>` : ""}
  <div class="row total"><span>TOTAL</span><span>$${Number(order.total).toFixed(2)}</span></div>
  <div class="sep"></div>
  <div class="center muted">Gracias por tu compra</div>
  <div class="center noprint" style="margin-top:12px;">
    <button class="btn" onclick="window.print()">Imprimir</button>
  </div>
  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 250));<\/script>
</body></html>`;
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) {
    toast.error("Habilita las ventanas emergentes para imprimir");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}`, { status });
      toast.success(`Pedido → ${STATUS_MAP[status]?.label}`);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const shown = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.key] = orders.filter((o) => o.status === s.key).length;
    return acc;
  }, {});

  const revenueToday = orders
    .filter((o) => o.status !== "cancelado" && new Date(o.created_at).toDateString() === new Date().toDateString())
    .reduce((sum, o) => sum + Number(o.total || 0), 0);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="orders-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Storefront size={14} className="text-emerald-400" weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A]">Pedidos</div>
        </div>
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl font-medium tracking-tighter">Órdenes de venta</h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Pedidos capturados automáticamente por la IA + creados manualmente.
            </p>
          </div>
          <div className="border border-[#27272A] bg-[#121214] px-5 py-3 rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Ventas hoy</div>
            <div className="font-display text-2xl font-medium tabular-nums" data-testid="orders-revenue-today">
              {money(revenueToday)}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          onClick={() => setFilter("all")}
          data-testid="filter-all"
          className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border transition-colors ${
            filter === "all" ? "border-[#5E6AD2] bg-[#5E6AD2]/10 text-[#FAFAFA]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]"
          }`}
        >
          Todos <span className="ml-1 text-[#71717A]">({orders.length})</span>
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            type="button"
            key={s.key}
            onClick={() => setFilter(s.key)}
            data-testid={`filter-${s.key}`}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border transition-colors ${
              filter === s.key ? `${s.bg} ${s.color}` : "border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]"
            }`}
          >
            {s.label} <span className="ml-1 opacity-60">({counts[s.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-[#71717A]">Cargando…</div>
      ) : shown.length === 0 ? (
        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-12 text-center">
          <Storefront size={32} className="text-[#3F3F46] mx-auto mb-3" />
          <div className="text-sm text-[#A1A1AA]">Aún no hay pedidos.</div>
          <div className="text-xs text-[#71717A] mt-1">
            Activa el modo Delivery en <span className="text-[#5E6AD2]">Agente IA</span> y la IA capturará los pedidos por WhatsApp.
          </div>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#121214] rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#27272A] text-[10px] uppercase tracking-[0.2em] text-[#71717A] font-medium">
            <div className="col-span-3">Cliente</div>
            <div className="col-span-4">Pedido</div>
            <div className="col-span-2">Dirección</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-2 text-right">Estado</div>
          </div>
          <div className="divide-y divide-[#1F1F22]">
            {shown.map((o) => {
              const s = STATUS_MAP[o.status] || STATUS_OPTIONS[0];
              const Icon = STATUS_ICON[o.status] || ClockCounterClockwise;
              return (
                <div
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className="grid grid-cols-12 gap-3 px-5 py-4 items-start hover:bg-[#0A0A0C] transition-colors"
                >
                  <div className="col-span-3 min-w-0">
                    <div className="text-[10px] text-[#71717A] font-mono-jb mb-0.5">#{o.order_number ?? o.id.slice(0, 6)}</div>
                    <div className="text-sm font-medium truncate">{o.contact_name || "Cliente"}</div>
                    <div className="text-[11px] text-[#71717A] font-mono-jb truncate">{o.contact_phone}</div>
                    <div className="text-[10px] text-[#5E6AD2] mt-1 flex items-center gap-1">
                      {o.source === "ai" && <span>IA</span>}
                      <span className="text-[#71717A]">· {fmtDate(o.created_at)}</span>
                    </div>
                  </div>
                  <div className="col-span-4 min-w-0">
                    {o.items && o.items.length ? (
                      <ul className="space-y-0.5">
                        {o.items.slice(0, 4).map((it, i) => (
                          <li key={i} className="text-xs text-[#D4D4D8] flex justify-between gap-2">
                            <span className="truncate">
                              <span className="text-[#71717A] mr-1">{it.qty}×</span>{it.name}
                            </span>
                            <span className="text-[#71717A] tabular-nums shrink-0">{money(it.price * it.qty)}</span>
                          </li>
                        ))}
                        {o.items.length > 4 && (
                          <li className="text-[10px] text-[#71717A]">+{o.items.length - 4} más</li>
                        )}
                      </ul>
                    ) : (
                      <span className="text-xs text-[#71717A] italic">(sin items)</span>
                    )}
                    {o.notes && <div className="text-[10px] text-amber-400 mt-1 italic">Nota: {o.notes}</div>}
                  </div>
                  <div className="col-span-2 min-w-0">
                    <div className="flex items-start gap-1 text-xs text-[#D4D4D8]">
                      <MapPin size={12} className="text-[#71717A] mt-0.5 shrink-0" />
                      <span className="line-clamp-3">{o.address || <em className="text-[#71717A]">(sin dirección)</em>}</span>
                    </div>
                    {o.delivery_fee > 0 && (
                      <div className="text-[10px] text-[#71717A] mt-1">Envío {money(o.delivery_fee)}</div>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <div className="font-display text-lg font-medium tabular-nums" data-testid={`order-total-${o.id}`}>
                      {money(o.total)}
                    </div>
                    {o.subtotal !== o.total && (
                      <div className="text-[10px] text-[#71717A] tabular-nums">sub {money(o.subtotal)}</div>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-end items-start">
                    <div className="flex flex-col items-end gap-1 w-full">
                      <div className="flex items-center gap-1.5 w-full justify-end">
                        <button
                          type="button"
                          onClick={() => printTicket(o)}
                          data-testid={`order-print-${o.id}`}
                          className="p-1.5 rounded-md border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#5E6AD2] hover:bg-[#5E6AD2]/10 transition-colors"
                          title="Imprimir ticket"
                        >
                          <Printer size={13} />
                        </button>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] uppercase tracking-wider ${s.bg} ${s.color}`}>
                          <Icon size={12} weight="fill" /> {s.label}
                        </div>
                      </div>
                      <select
                        value={o.status}
                        onChange={(e) => updateStatus(o.id, e.target.value)}
                        data-testid={`order-status-select-${o.id}`}
                        className="mt-1 bg-[#0A0A0C] border border-[#27272A] rounded-md px-2 py-1 text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:border-[#3F3F46] focus:border-[#5E6AD2] focus:outline-none"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
